import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { useQuery } from 'react-query';

import { ACTIVE_PROMO_ID_COOKIE, COUPON_COOKIE } from '~/features/cookies/constants';
import { getCookieDomain } from '~/features/cookies/utils/get-cookie-domain';
import { useClientRender } from '~/hooks/use-client-render';

import { getPromotion } from '../controllers/get-promotion';
import { singleQueryParamValueSchema } from '../models/promotions';

export function usePromotion() {
  const { asPath, query, isReady } = useRouter();
  const promo = singleQueryParamValueSchema.parse(query.promo);
  const promoParticipation = singleQueryParamValueSchema.parse(query.promo_participation);
  const forcePromo = singleQueryParamValueSchema.parse(query.force_promo);
  const pathname = asPath?.replace(/(\?.+$|#.+$)/, ''); // Note: we want to remove the query params and any hashes from the pathname to prevent refetching if these change.
  const isClient = useClientRender();
  const cookiedCoupon = isClient ? Cookies.get(COUPON_COOKIE) : undefined;
  const couponFromQueryParam = query?.coupon as string | undefined;

  const result = useQuery(
    ['promotions', pathname, promo, promoParticipation, forcePromo],
    async () => {
      const promotion = await getPromotion({
        pathname,
        promo,
        promoParticipation,
        forcePromo,
      });

      if (!promotion) {
        return null;
      }

      if (promotion.promotionId) {
        // Note: cookie here is used to track the active promo code for the user.
        Cookies.set(ACTIVE_PROMO_ID_COOKIE, promotion.promotionId);
      }

      if (!couponFromQueryParam && !cookiedCoupon && promotion.promoCode) {
        Cookies.set(COUPON_COOKIE, promotion.promoCode, { maxAge: '604800', domain: getCookieDomain() }); // 604800 seconds = 1 week
      }

      return promotion;
    },
    {
      enabled: isReady,
    }
  );

  return result;
}
